import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Device = {
  uuid: string;
  label: string;
  category: string | null;
  make: string | null;
  model: string | null;
  serial: string | null;
  status: string;
  owner: string | null;
  internet_capable: number;
  image_capable: number;
  is_router?: number;
  workplace?: number;
  work_use_started?: string | null;
  acquired_date: string | null;
  notes: string | null;
  ledger_seq: number;
  last_reviewed_at: string | null;
};

const REVIEW_DUE_DAYS = 182; // ~6 months between "confirm current" checkpoints
const MS_PER_DAY = 86400000;
function isReviewDue(last_reviewed_at: string | null): boolean {
  if (!last_reviewed_at) return true;
  return Date.now() - new Date(last_reviewed_at).getTime() > REVIEW_DUE_DAYS * MS_PER_DAY;
}
function nextReviewDate(last_reviewed_at: string): string {
  return new Date(new Date(last_reviewed_at).getTime() + REVIEW_DUE_DAYS * MS_PER_DAY).toISOString().slice(0, 10);
}
function fmtReviewDate(ts: string): string {
  return ts.slice(0, 10);
}
const DISPOSED_STATUSES = ['disposed', 'sold', 'lost', 'stolen', 'returned'];

type HistoryEntry = { kind: 'record' | 'lifecycle'; seq: number; date: string; title: string; detail: string | null };

type Ev = {
  id: number;
  device_label: string;
  device_serial: string | null;
  event_type: string;
  event_date: string;
  detail: string | null;
  ledger_seq: number;
  recorded_at: string;
};

const EVENT_TYPES = ['acquired', 'in_use', 'repaired', 'lost', 'stolen', 'disposed', 'returned', 'note'] as const;
const STATUSES = ['in_use', 'stored', 'repaired', 'awaiting_disposal', 'lost', 'stolen', 'disposed', 'returned', 'sold'] as const;
const OWNERS = ['self', 'partner', 'business'] as const;
const KIND: Record<string, string> = {
  acquired: 'ok', in_use: 'mute', stored: 'mute', repaired: 'warn', returned: 'mute', sold: 'mute',
  awaiting_disposal: 'warn', disposed: 'warn', lost: 'bad', stolen: 'bad', note: 'mute',
};
const todayISO = () => new Date().toISOString().slice(0, 10);

export function Devices() {
  const [tab, setTab] = useState<'inventory' | 'events'>('inventory');
  const [devices, setDevices] = useState<Device[]>([]);
  const [events, setEvents] = useState<Ev[]>([]);

  // inventory filtering / sorting
  const [q, setQ] = useState('');
  const [fStatus, setFStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [fOwner, setFOwner] = useState<'all' | string>('all');
  const [fCap, setFCap] = useState<Set<'internet' | 'camera' | 'router' | 'review_due'>>(new Set());
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'review'>('recent');
  function toggleCap(c: 'internet' | 'camera' | 'router' | 'review_due') {
    setFCap((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });
  }

  // device-record form
  const [d, setD] = useState({
    label: '', make: '', model: '', serial: '', category: '', owner: 'self',
    status: 'in_use', acquired_date: todayISO(), internet_capable: true, image_capable: false, notes: '',
    workplace: false, work_use_started: todayISO(), is_router: false,
  });
  const [dBusy, setDBusy] = useState(false);
  const [dErr, setDErr] = useState<string | null>(null);

  // event form
  const [label, setLabel] = useState('');
  const [serial, setSerial] = useState('');
  const [type, setType] = useState<string>('acquired');
  const [date, setDate] = useState(todayISO());
  const [detail, setDetail] = useState('');
  const [evBusy, setEvBusy] = useState(false);
  const [evErr, setEvErr] = useState<string | null>(null);
  const eventFormRef = useRef<HTMLFormElement>(null);

  // per-device edit state (status + note)
  const [editingNote, setEditingNote] = useState<Record<string, string>>({});
  const [editingStatus, setEditingStatus] = useState<Record<string, string>>({});
  const [editWork, setEditWork] = useState<Record<string, boolean>>({});
  const [editWorkDate, setEditWorkDate] = useState<Record<string, string>>({});
  const [noteOpen, setNoteOpen] = useState<Record<string, boolean>>({});
  const [noteBusy, setNoteBusy] = useState<Record<string, boolean>>({});

  // per-device history panel
  const [historyOpen, setHistoryOpen] = useState<Record<string, boolean>>({});
  const [historyData, setHistoryData] = useState<Record<string, HistoryEntry[]>>({});
  async function toggleHistory(uuid: string) {
    const open = !historyOpen[uuid];
    setHistoryOpen((p) => ({ ...p, [uuid]: open }));
    if (open && !historyData[uuid]) {
      try {
        const r = await fetch(`/api/devices/${uuid}/history`).then((x) => x.json());
        setHistoryData((p) => ({ ...p, [uuid]: r.timeline ?? [] }));
      } catch { /* offline */ }
    }
  }

  function invalidateHistory(uuid: string) {
    setHistoryData((p) => { const n = { ...p }; delete n[uuid]; return n; });
    if (historyOpen[uuid]) void refreshHistory(uuid);
  }
  async function refreshHistory(uuid: string) {
    try {
      const r = await fetch(`/api/devices/${uuid}/history`).then((x) => x.json());
      setHistoryData((p) => ({ ...p, [uuid]: r.timeline ?? [] }));
    } catch { /* offline */ }
  }

  async function confirmReview(uuid: string) {
    try {
      const res = await fetch(`/api/devices/${uuid}/review`, { method: 'POST' });
      if (!res.ok) { const j = await res.json().catch(() => ({})); if (j.error) alert(j.error); }
      await loadDevices();
      invalidateHistory(uuid);
    } catch { /* offline */ }
  }

  async function saveEdit(uuid: string) {
    setNoteBusy((p) => ({ ...p, [uuid]: true }));
    try {
      const body: { notes: string; status?: string; workplace?: boolean; work_use_started?: string } = { notes: editingNote[uuid] ?? '' };
      if (editingStatus[uuid]) body.status = editingStatus[uuid];
      body.workplace = !!editWork[uuid];
      if (editWork[uuid]) body.work_use_started = editWorkDate[uuid] || todayISO();
      const res = await fetch(`/api/devices/${uuid}`, {
        method: 'PATCH', headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); if (j.error) alert(j.error); }
      setNoteOpen((p) => ({ ...p, [uuid]: false }));
      await loadDevices();
      invalidateHistory(uuid);
    } catch { /* offline */ }
    finally { setNoteBusy((p) => ({ ...p, [uuid]: false })); }
  }

  async function loadDevices() {
    try { setDevices((await fetch('/api/devices').then((x) => x.json())).devices ?? []); } catch { /* offline */ }
  }
  async function loadEvents() {
    try { setEvents((await fetch('/api/device-events').then((x) => x.json())).events ?? []); } catch { /* offline */ }
  }
  useEffect(() => { loadDevices(); loadEvents(); }, []);

  // A serial/IMEI is enforced unique server-side. When the new device has NO serial,
  // there's nothing to dedup on, so warn (don't block) if the name already exists.
  const dupNameNoSerial = !d.serial.trim() && d.label.trim().length > 0
    && devices.some((x) => x.label.trim().toLowerCase() === d.label.trim().toLowerCase());

  async function addDevice(e: FormEvent) {
    e.preventDefault();
    setDErr(null);
    if (!d.label.trim()) { setDErr('Device name is required'); return; }
    if (dupNameNoSerial && !window.confirm(`You already have a device named “${d.label.trim()}”, and this one has no serial/IMEI to tell them apart. Add it anyway?`)) return;
    setDBusy(true);
    try {
      const res = await fetch('/api/devices', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          label: d.label, make: d.make || undefined, model: d.model || undefined, serial: d.serial || undefined,
          category: d.category || undefined, owner: d.owner, status: d.status,
          acquired_date: d.acquired_date || undefined,
          internet_capable: d.internet_capable, image_capable: d.image_capable, notes: d.notes || undefined,
          workplace: d.workplace, work_use_started: d.workplace ? (d.work_use_started || undefined) : undefined,
          is_router: d.is_router,
        }),
      });
      if (!res.ok) { setDErr((await res.json()).error ?? 'Failed to add device'); return; }
      setD({ label: '', make: '', model: '', serial: '', category: '', owner: 'self',
        status: 'in_use', acquired_date: todayISO(), internet_capable: true, image_capable: false, notes: '',
        workplace: false, work_use_started: todayISO(), is_router: false });
      await loadDevices();
    } catch { setDErr('Could not reach the server'); }
    finally { setDBusy(false); }
  }

  function logEventFor(dev: Device, presetType?: string) {
    setLabel(dev.label);
    setSerial(dev.serial ?? '');
    setType(presetType ?? 'disposed');
    setDate(todayISO());
    setDetail('');
    setTab('events');
    setTimeout(() => eventFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function addEvent(e: FormEvent) {
    e.preventDefault();
    setEvErr(null);
    if (!label.trim()) { setEvErr('Device name is required'); return; }
    setEvBusy(true);
    try {
      const res = await fetch('/api/device-events', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ device_label: label, device_serial: serial, event_type: type, event_date: date, detail }),
      });
      if (!res.ok) { setEvErr((await res.json()).error ?? 'Failed to record'); return; }
      setLabel(''); setSerial(''); setDetail(''); setType('acquired'); setDate(todayISO());
      await loadEvents();
      await loadDevices();           // an event may have synced a device's status
      setHistoryData({});            // any device's history may now be stale
    } catch { setEvErr('Could not reach the server'); }
    finally { setEvBusy(false); }
  }

  const isActive = (x: Device) => !DISPOSED_STATUSES.includes(x.status);
  // 'awaiting_disposal' is still held (so it stays on notifications), but it is flagged
  // for removal — no 6-month "confirm current" nag applies to it.
  const isReviewable = (x: Device) => isActive(x) && x.status !== 'awaiting_disposal';
  const active = devices.filter(isActive).length;
  const reviewDueCount = devices.filter((x) => isReviewable(x) && isReviewDue(x.last_reviewed_at)).length;
  const ownerOptions = useMemo(() => Array.from(new Set(devices.map((x) => x.owner ?? 'self'))).sort(), [devices]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = devices.filter((x) => {
      if (fStatus === 'active' && !isActive(x)) return false;
      if (fStatus === 'inactive' && isActive(x)) return false;
      if (fOwner !== 'all' && (x.owner ?? 'self') !== fOwner) return false;
      if (fCap.has('internet') && !x.internet_capable) return false;
      if (fCap.has('camera') && !x.image_capable) return false;
      if (fCap.has('router') && !x.is_router) return false;
      if (fCap.has('review_due') && !(isActive(x) && isReviewDue(x.last_reviewed_at))) return false;
      if (needle) {
        const hay = [x.label, x.make, x.model, x.serial, x.category, x.owner, x.notes].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    out.sort((a, b) => {
      if (sortBy === 'name') return a.label.localeCompare(b.label);
      if (sortBy === 'review') { // most-overdue first; never-reviewed counts as oldest
        const ra = a.last_reviewed_at ? new Date(a.last_reviewed_at).getTime() : 0;
        const rb = b.last_reviewed_at ? new Date(b.last_reviewed_at).getTime() : 0;
        return ra - rb;
      }
      return b.ledger_seq - a.ledger_seq; // recent
    });
    return out;
  }, [devices, q, fStatus, fOwner, fCap, sortBy]);
  const hasFilters = q.trim() !== '' || fStatus !== 'all' || fOwner !== 'all' || fCap.size > 0;
  function clearFilters() { setQ(''); setFStatus('all'); setFOwner('all'); setFCap(new Set()); }

  return (
    <Page
      eyebrow="Records"
      title="Devices"
      actions={<span className="pill pill-ok"><span className="dot dot-ok" />append-only · anchored</span>}
    >
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        <button className={`btn ${tab === 'inventory' ? 'btn-primary' : 'btn-ghost'}`} type="button" onClick={() => setTab('inventory')}>
          Inventory ({devices.length})
        </button>
        <button className={`btn ${tab === 'events' ? 'btn-primary' : 'btn-ghost'}`} type="button" onClick={() => setTab('events')}>
          Event log ({events.length})
        </button>
      </div>

      {tab === 'inventory' && (
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, alignItems: 'start' }} className="dev-grid">
          <form className="card card-pad" onSubmit={addDevice}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Add a device to the inventory</h3>
            <p className="muted" style={{ fontSize: 12.5, marginTop: 0 }}>
              Your authoritative list of what you own. This is the list reconciled against the police’s.
              An internet-capable device with an acquisition date raises a 3-day notify reminder (R2).
            </p>
            <label className="fld"><span>Device name</span>
              <input value={d.label} onChange={(e) => setD({ ...d, label: e.target.value })} placeholder="e.g. Samsung Galaxy S21 Ultra" />
            </label>
            {dupNameNoSerial && (
              <div style={{ color: 'var(--warn)', fontSize: 11.5, marginTop: -4, marginBottom: 4 }}>
                ⚠ A device named “{d.label.trim()}” already exists. Add a serial/IMEI to tell them apart, or continue to add it anyway.
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Make</span>
                <input value={d.make} onChange={(e) => setD({ ...d, make: e.target.value })} placeholder="Samsung" />
              </label>
              <label className="fld" style={{ flex: 1 }}><span>Model</span>
                <input value={d.model} onChange={(e) => setD({ ...d, model: e.target.value })} placeholder="SM-G998B" />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Type / category</span>
                <input value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })} placeholder="phone, laptop, camera…" />
              </label>
              <label className="fld" style={{ flex: 1 }}><span>Serial / IMEI</span>
                <input value={d.serial} onChange={(e) => setD({ ...d, serial: e.target.value })} placeholder="if it has one" />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Owner</span>
                <select value={d.owner} onChange={(e) => setD({ ...d, owner: e.target.value })}>
                  {OWNERS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label className="fld" style={{ flex: 1 }}><span>Status</span>
                <select value={d.status} onChange={(e) => setD({ ...d, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </label>
            </div>
            <label className="fld"><span>Acquired date</span>
              <input type="date" value={d.acquired_date} max={todayISO()} onChange={(e) => setD({ ...d, acquired_date: e.target.value })} />
            </label>
            <div style={{ display: 'flex', gap: 16, margin: '2px 0 8px' }}>
              <label className="muted" style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={d.internet_capable} onChange={(e) => setD({ ...d, internet_capable: e.target.checked })} />
                internet-capable
              </label>
              <label className="muted" style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={d.image_capable} onChange={(e) => setD({ ...d, image_capable: e.target.checked })} />
                camera / image-capable
              </label>
              <label className="muted" style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={d.workplace} onChange={(e) => setD({ ...d, workplace: e.target.checked })} />
                workplace computer (employer's)
              </label>
              <label className="muted" style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={d.is_router} onChange={(e) => setD({ ...d, is_router: e.target.checked })} />
                home broadband router
              </label>
            </div>
            {d.workplace && (
              <label className="fld"><span>Date you started using it for work</span>
                <input type="date" value={d.work_use_started} max={todayISO()} onChange={(e) => setD({ ...d, work_use_started: e.target.value })} />
                <small className="muted3" style={{ fontSize: 11 }}>
                  SHPO §1 work exemption: a computer at your place of work must be notified to the PPU within 3 days of you commencing use — this starts that reminder. (The §1 acquire-notify rule for your own devices does not apply to it, but §2–§12 still do.)
                </small>
              </label>
            )}
            <label className="fld"><span>Notes (optional)</span>
              <textarea value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} rows={2} placeholder="room, condition, lifecycle notes" />
            </label>
            {dErr && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '4px 0' }}>{dErr}</div>}
            <button className="btn btn-primary" type="submit" disabled={dBusy} style={{ width: '100%', justifyContent: 'center' }}>
              {dBusy ? 'Adding…' : 'Add device'}
            </button>
          </form>

          <div className="card">
            <div className="panel-head">
              <h3>Device inventory</h3>
              <span className="hint" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {devices.length} on record · {active} active{reviewDueCount > 0 ? ` · ${reviewDueCount} review due` : ''}
                {devices.length - active > 0 && (
                  <a className="btn btn-ghost" href="/api/export/former-devices.pdf" download style={{ fontSize: 12, padding: '3px 9px' }}>
                    Former devices report ({devices.length - active}) ·.pdf
                  </a>
                )}
              </span>
            </div>

            <div className="dev-toolbar">
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, make, serial, notes…" />
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value as typeof fStatus)} title="Status">
                <option value="all">All statuses</option>
                <option value="active">Active only</option>
                <option value="inactive">Disposed / lost / sold</option>
              </select>
              {ownerOptions.length > 1 && (
                <select value={fOwner} onChange={(e) => setFOwner(e.target.value)} title="Owner">
                  <option value="all">All owners</option>
                  {ownerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              <span className={`chip chip-btn${fCap.has('internet') ? ' on' : ''}`} onClick={() => toggleCap('internet')}>internet</span>
              <span className={`chip chip-btn${fCap.has('camera') ? ' on' : ''}`} onClick={() => toggleCap('camera')}>camera</span>
              <span className={`chip chip-btn${fCap.has('router') ? ' on' : ''}`} onClick={() => toggleCap('router')}>router</span>
              <span className={`chip chip-btn${fCap.has('review_due') ? ' on' : ''}`} onClick={() => toggleCap('review_due')}>review due</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} title="Sort" style={{ marginLeft: 'auto' }}>
                <option value="recent">Newest first</option>
                <option value="name">Name A–Z</option>
                <option value="review">Review oldest first</option>
              </select>
              {hasFilters && <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: '4px 10px' }} onClick={clearFilters}>Clear</button>}
            </div>

            <div className="ledger">
              {devices.length === 0 && (
                <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>
                  No devices yet — add your first on the left. (The full inventory will normally be loaded from your device list.)
                </div>
              )}
              {devices.length > 0 && filtered.length === 0 && (
                <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>
                  No devices match these filters. <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: '2px 8px' }} onClick={clearFilters}>Clear filters</button>
                </div>
              )}
              {(hasFilters || sortBy !== 'recent') && filtered.length > 0 && (
                <div style={{ padding: '6px 14px', fontSize: 11.5, color: 'var(--ink3)', borderBottom: '1px solid var(--border)' }}>
                  Showing {filtered.length} of {devices.length}
                </div>
              )}
              {filtered.map((dev) => {
                const activeDev = isActive(dev);
                const reviewable = isReviewable(dev); // not disposed/lost/sold, and not awaiting-disposal
                const due = reviewable && isReviewDue(dev.last_reviewed_at);
                const isNoteOpen = noteOpen[dev.uuid] ?? false;
                return (
                <div className="ledger-row" key={dev.uuid} style={{ flexWrap: 'wrap', gap: '8px 10px', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 96 }}>
                    <span className={`badge badge-${KIND[dev.status] ?? 'mute'}`}>{dev.status.replace('_', ' ').toUpperCase()}</span>
                    {reviewable && (due
                      ? <span className="badge badge-warn">REVIEW DUE</span>
                      : <span className="badge badge-ok" title={`Confirmed current ${fmtReviewDate(dev.last_reviewed_at!)}`}>CURRENT</span>)}
                  </div>

                  <div className="ledger-main" style={{ flex: '1 1 240px' }}>
                    <div className="ledger-text">
                      <strong style={{ cursor: 'pointer' }} title="View this device’s full history" onClick={() => toggleHistory(dev.uuid)}>{dev.label}</strong>
                      {(dev.make || dev.model) ? <span className="muted"> · {[dev.make, dev.model].filter(Boolean).join(' ')}</span> : null}
                      {dev.serial ? <span className="mono muted3"> · {dev.serial}</span> : null}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '5px 0 3px' }}>
                      {dev.category ? <span className="chip">{dev.category}</span> : null}
                      <span className="chip">{dev.owner ?? 'self'}</span>
                      {dev.internet_capable ? <span className="chip chip-info">internet</span> : null}
                      {dev.image_capable ? <span className="chip chip-info">camera</span> : null}
                      {dev.is_router ? <span className="chip chip-info">router</span> : null}
                    </div>
                    <div className="ledger-time">
                      {dev.acquired_date ? `acquired ${dev.acquired_date} · ` : ''}
                      {dev.status === 'awaiting_disposal' ? 'awaiting PPU check before disposal · ' : ''}
                      {reviewable && (due
                        ? (dev.last_reviewed_at ? `review due (last ${fmtReviewDate(dev.last_reviewed_at)}) · ` : 'never reviewed — confirm it’s still current · ')
                        : `next check due ${nextReviewDate(dev.last_reviewed_at!)} · `)}
                      seq {dev.ledger_seq}
                    </div>
                    {dev.notes && !isNoteOpen && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{dev.notes}</div>
                    )}
                    {isNoteOpen && (
                      <div style={{ marginTop: 6, padding: 10, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                          <span className="muted" style={{ fontSize: 11.5 }}>Status</span>
                          <select
                            value={editingStatus[dev.uuid] ?? dev.status}
                            onChange={(e) => setEditingStatus((p) => ({ ...p, [dev.uuid]: e.target.value }))}
                            style={{ fontSize: 12.5, padding: '3px 8px' }}
                          >
                            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                          </select>
                          {DISPOSED_STATUSES.includes(editingStatus[dev.uuid] ?? dev.status) && (
                            <span className="muted3" style={{ fontSize: 11 }}>removes it from your active inventory & review list</span>
                          )}
                          {(editingStatus[dev.uuid] ?? dev.status) === 'awaiting_disposal' && (
                            <span className="muted3" style={{ fontSize: 11 }}>kept on your notifications (still held) — no review nag until disposed</span>
                          )}
                        </div>
                        <textarea
                          rows={2}
                          style={{ width: '100%', fontSize: 12.5, boxSizing: 'border-box' }}
                          placeholder="Note (e.g. why disposed, who owns it)"
                          value={editingNote[dev.uuid] ?? (dev.notes ?? '')}
                          onChange={(e) => setEditingNote((p) => ({ ...p, [dev.uuid]: e.target.value }))}
                        />
                        <label className="fld" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                          <input type="checkbox" checked={!!editWork[dev.uuid]} onChange={(e) => setEditWork((p) => ({ ...p, [dev.uuid]: e.target.checked }))} style={{ width: 'auto', margin: 0 }} />
                          <span style={{ margin: 0, fontSize: 12.5 }}>This is a <b>workplace computer</b> (employer’s) — notify PPU within 3 days of commencing use (R4)</span>
                        </label>
                        {editWork[dev.uuid] && (
                          <label className="fld" style={{ marginTop: 6, maxWidth: 220 }}><span>Date you started using it for work</span>
                            <input type="date" max={todayISO()} value={editWorkDate[dev.uuid] ?? todayISO()} onChange={(e) => setEditWorkDate((p) => ({ ...p, [dev.uuid]: e.target.value }))} />
                          </label>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button className="btn btn-primary" type="button" disabled={noteBusy[dev.uuid]}
                            style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => saveEdit(dev.uuid)}>
                            {noteBusy[dev.uuid] ? 'Saving…' : 'Save changes'}
                          </button>
                          <button className="btn btn-ghost" type="button"
                            style={{ fontSize: 12, padding: '4px 12px' }}
                            onClick={() => setNoteOpen((p) => ({ ...p, [dev.uuid]: false }))}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {historyOpen[dev.uuid] && (
                      <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8 }}>
                        <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Full history</div>
                        {!historyData[dev.uuid] && <div className="muted3" style={{ fontSize: 12.5 }}>Loading…</div>}
                        {historyData[dev.uuid]?.length === 0 && <div className="muted3" style={{ fontSize: 12.5 }}>No history yet.</div>}
                        {historyData[dev.uuid]?.map((h, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, padding: '3px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                            <span className="mono muted3" style={{ fontSize: 11.5, minWidth: 78, flexShrink: 0 }}>{h.date}</span>
                            <span className={`chip ${h.kind === 'lifecycle' ? 'chip-info' : ''}`} style={{ alignSelf: 'flex-start', textTransform: 'capitalize' }}>{h.kind === 'lifecycle' ? h.title : 'record'}</span>
                            <span style={{ fontSize: 12.5 }}>
                              {h.kind === 'record' ? h.title : (h.detail ?? h.title)}
                              {h.kind === 'lifecycle' && h.detail ? null : null}
                              <span className="muted3" style={{ fontSize: 11 }}> · seq {h.seq}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignSelf: 'flex-start' }}>
                    {due && (
                      <button className="btn btn-primary" type="button" style={{ fontSize: 12, padding: '4px 10px' }}
                        title="Records a dated, anchored review confirming you still hold this device. Hidden again until the next 6-month checkpoint."
                        onClick={() => confirmReview(dev.uuid)}>
                        Confirm current
                      </button>
                    )}
                    <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: '4px 10px' }}
                      onClick={() => {
                        setEditingNote((p) => ({ ...p, [dev.uuid]: dev.notes ?? '' }));
                        setEditingStatus((p) => ({ ...p, [dev.uuid]: dev.status }));
                        setEditWork((p) => ({ ...p, [dev.uuid]: !!dev.workplace }));
                        setEditWorkDate((p) => ({ ...p, [dev.uuid]: dev.work_use_started ?? todayISO() }));
                        setNoteOpen((p) => ({ ...p, [dev.uuid]: !p[dev.uuid] }));
                      }}>
                      {isNoteOpen ? 'Close' : 'Edit'}
                    </button>
                    <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => toggleHistory(dev.uuid)}>
                      {historyOpen[dev.uuid] ? 'Hide history' : 'History'}
                    </button>
                    <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => logEventFor(dev)}>
                      Log event
                    </button>
                  </div>
                  <Attachments entityType="device" entityUuid={dev.uuid} />
                </div>
                );
              })}
            </div>
            <div className="panel-foot mono">each device = one append-only ledger row · “Confirm current” reappears at each 6-month checkpoint</div>
          </div>
        </div>
      )}

      {tab === 'events' && (
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, alignItems: 'start' }} className="dev-grid">
          <form className="card card-pad" onSubmit={addEvent} ref={eventFormRef}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Record a device event</h3>
            <p className="muted" style={{ fontSize: 12.5, marginTop: 0 }}>
              Each event is dated and written to the tamper-evident ledger. A disposal/return/loss raises
              a 3-day notify reminder (R3). Use “repaired” for things like a replaced hard drive — so a
              hardware fix can’t be misread as deleting history. A disposed/lost/stolen/returned event
              also updates the matching device’s status in your inventory automatically.
            </p>
            <label className="fld"><span>Device</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Photo-booth PC (Snap The Day)" />
            </label>
            <label className="fld"><span>Serial / IMEI (optional)</span>
              <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="if it has one" />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <label className="fld" style={{ flex: 1 }}><span>Event</span>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
              </label>
              <label className="fld" style={{ flex: 1 }}><span>Date it happened</span>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} max={todayISO()} />
              </label>
            </div>
            <label className="fld"><span>Detail (optional)</span>
              <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={3}
                placeholder="e.g. hard drive failed, replaced with a new SSD; old drive retained" />
            </label>
            {evErr && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '4px 0' }}>{evErr}</div>}
            <button className="btn btn-primary" type="submit" disabled={evBusy} style={{ width: '100%', justifyContent: 'center' }}>
              {evBusy ? 'Recording…' : 'Record event'}
            </button>
          </form>

          <div className="card">
            <div className="panel-head"><h3>Event log</h3><span className="hint">{events.length} events · newest first</span></div>
            <div className="ledger">
              {events.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>No device events yet — record one on the left.</div>}
              {events.map((e) => (
                <div className="ledger-row" key={e.id}>
                  <span className={`badge badge-${KIND[e.event_type] ?? 'mute'}`}>{e.event_type.replace('_', ' ').toUpperCase()}</span>
                  <div className="ledger-main">
                    <div className="ledger-text">
                      <strong>{e.device_label}</strong>{e.device_serial ? <span className="mono muted3"> · {e.device_serial}</span> : null}
                      {e.detail ? <span className="muted"> — {e.detail}</span> : null}
                    </div>
                    <div className="ledger-time">{e.event_date} · anchored at ledger seq {e.ledger_seq}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="panel-foot mono">every event = one append-only ledger row</div>
          </div>
        </div>
      )}
    </Page>
  );
}
